import React, { useState, useEffect, useCallback } from 'react';
import {
  Sliders,
  Save,
  RotateCcw,
  Flame,
  Zap,
  Thermometer,
  Snowflake,
  Info,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';



// ─── Default Configuration ────────────────────────────────────────────────────

const DEFAULT_CONFIG = {
  // Score thresholds
  thresholds: {
    veryHot: 60,   // score >= veryHot → Très Chaud 🔥
    hot: 35,       // score >= hot     → Chaud ⚡
    warm: 15,      // score >= warm    → Tiède
    // below warm → Froid
  },
  // Price range scoring
  pricing: {
    tier1Min: 100_000_000,  // 100M+ XOF → +40 pts
    tier1Points: 40,
    tier2Min: 50_000_000,   // 50M–100M  → +25 pts
    tier2Points: 25,
    tier3Min: 20_000_000,   // 20M–50M   → +10 pts
    tier3Points: 10,
  },
  // Message length scoring
  messaging: {
    minLength: 50,          // message > minLength chars → +10 pts
    lengthPoints: 10,
  },
  // Phone presence scoring
  phone: {
    points: 15,             // has phone → +15 pts
  },
  // GA4 event weight multipliers (applied to base score)
  ga4Weights: {
    appointment_booked: 35,
    generate_lead: 20,
    roi_calculation: 20,
    inquiry_created: 10,
    property_view: 5,
    alert_signup: 5,
  },
};

const STORAGE_KEY = 'kapital_lead_scoring_config';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const loadConfig = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONFIG;
    return JSON.parse(raw);
  } catch {
    return DEFAULT_CONFIG;
  }
};

const saveConfig = (cfg) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
};

// Export for use in adminNotificationService
export const getLeadScoringConfig = loadConfig;

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionCard = ({ title, icon: SectionIcon, iconColor, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColor}`}>
            <SectionIcon className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-gray-900">{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-6 pb-6 pt-2 border-t border-gray-100">{children}</div>}
    </div>
  );
};

const NumberField = ({ label, hint, value, onChange, min = 0, max = 999999999, step = 1, suffix = '' }) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e?.target?.value))}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
      />
      {suffix && <span className="text-sm text-gray-500 whitespace-nowrap">{suffix}</span>}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const LeadScoringConfigPanel = () => {
  const [config, setConfig] = useState(loadConfig);
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Deep compare to detect changes
  useEffect(() => {
    const stored = JSON.stringify(loadConfig());
    setHasChanges(JSON.stringify(config) !== stored);
  }, [config]);

  const update = useCallback((path, value) => {
    setConfig((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path?.split('.');
      let ref = next;
      for (let i = 0; i < keys?.length - 1; i++) ref = ref?.[keys?.[i]];
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  }, []);

  const handleSave = () => {
    saveConfig(config);
    setSaved(true);
    setHasChanges(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    setConfig(DEFAULT_CONFIG);
  };

  // Live score preview
  const previewScore = (price, hasPhone, msgLen, eventKey) => {
    let score = 0;
    if (price >= config?.pricing?.tier1Min) score += config?.pricing?.tier1Points;
    else if (price >= config?.pricing?.tier2Min) score += config?.pricing?.tier2Points;
    else if (price >= config?.pricing?.tier3Min) score += config?.pricing?.tier3Points;
    if (hasPhone) score += config?.phone?.points;
    if (msgLen > config?.messaging?.minLength) score += config?.messaging?.lengthPoints;
    if (eventKey) score += config?.ga4Weights?.[eventKey] || 0;
    return score;
  };

  const getLabel = (score) => {
    if (score >= config?.thresholds?.veryHot) return { label: 'Très Chaud 🔥', cls: 'bg-red-100 text-red-700' };
    if (score >= config?.thresholds?.hot) return { label: 'Chaud ⚡', cls: 'bg-orange-100 text-orange-700' };
    if (score >= config?.thresholds?.warm) return { label: 'Tiède', cls: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Froid', cls: 'bg-blue-100 text-blue-700' };
  };

  const previewCases = [
    { label: 'Propriété 120M + téléphone + RDV', price: 120_000_000, phone: true, msgLen: 0, event: 'appointment_booked' },
    { label: 'Propriété 60M + message long', price: 60_000_000, phone: false, msgLen: 80, event: 'inquiry_created' },
    { label: 'Propriété 25M + calcul ROI', price: 25_000_000, phone: false, msgLen: 0, event: 'roi_calculation' },
    { label: 'Propriété 5M, pas de téléphone', price: 5_000_000, phone: false, msgLen: 0, event: null },
  ];

  const ga4EventLabels = {
    appointment_booked: 'Rendez-vous réservé',
    generate_lead: 'Lead généré',
    roi_calculation: 'Calcul ROI',
    inquiry_created: 'Demande créée',
    property_view: 'Vue propriété',
    alert_signup: 'Inscription alerte',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Sliders className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Configuration Scoring des Leads</h2>
              <p className="text-slate-300 text-sm mt-0.5">
                Ajustez les seuils et multiplicateurs sans modifier le code
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Réinitialiser
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                hasChanges
                  ? 'bg-blue-500 hover:bg-blue-400 text-white shadow-lg'
                  : 'bg-white/10 text-white/50 cursor-not-allowed'
              }`}
            >
              {saved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {saved ? 'Sauvegardé !' : 'Sauvegarder'}
            </button>
          </div>
        </div>
        {saved && (
          <div className="mt-4 flex items-center gap-2 bg-green-500/20 border border-green-400/30 rounded-lg px-4 py-2 text-green-200 text-sm">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            Configuration sauvegardée. Les nouveaux leads seront scorés avec ces paramètres.
          </div>
        )}
        {hasChanges && !saved && (
          <div className="mt-4 flex items-center gap-2 bg-amber-500/20 border border-amber-400/30 rounded-lg px-4 py-2 text-amber-200 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Modifications non sauvegardées — cliquez sur "Sauvegarder" pour appliquer.
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: config sections */}
        <div className="lg:col-span-2 space-y-4">

          {/* Score Thresholds */}
          <SectionCard title="Seuils de Classification" icon={Thermometer} iconColor="bg-purple-500">
            <p className="text-xs text-gray-500 mb-4 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              Un lead est classé selon son score total. Définissez les scores minimaux pour chaque catégorie.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Flame className="w-4 h-4 text-red-500" />
                  <span className="font-semibold text-red-700 text-sm">Très Chaud 🔥</span>
                </div>
                <NumberField
                  label="Score minimum"
                  value={config?.thresholds?.veryHot}
                  onChange={(v) => update('thresholds.veryHot', v)}
                  min={1}
                  max={200}
                  suffix="pts"
                />
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-orange-500" />
                  <span className="font-semibold text-orange-700 text-sm">Chaud ⚡</span>
                </div>
                <NumberField
                  label="Score minimum"
                  value={config?.thresholds?.hot}
                  onChange={(v) => update('thresholds.hot', v)}
                  min={1}
                  max={200}
                  suffix="pts"
                />
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Thermometer className="w-4 h-4 text-yellow-500" />
                  <span className="font-semibold text-yellow-700 text-sm">Tiède</span>
                </div>
                <NumberField
                  label="Score minimum"
                  value={config?.thresholds?.warm}
                  onChange={(v) => update('thresholds.warm', v)}
                  min={1}
                  max={200}
                  suffix="pts"
                />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
              <Snowflake className="w-3 h-3" />
              En dessous de {config?.thresholds?.warm} pts → Froid
            </p>
          </SectionCard>

          {/* Price Range Scoring */}
          <SectionCard title="Fourchette de Prix" icon={Flame} iconColor="bg-amber-500">
            <p className="text-xs text-gray-500 mb-4 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              Points attribués selon la valeur de la propriété (en XOF). Les paliers sont évalués du plus élevé au plus bas.
            </p>
            <div className="space-y-4">
              {[
                { label: 'Palier 1 — Prix minimum', minKey: 'pricing.tier1Min', ptsKey: 'pricing.tier1Points', color: 'text-red-600' },
                { label: 'Palier 2 — Prix minimum', minKey: 'pricing.tier2Min', ptsKey: 'pricing.tier2Points', color: 'text-orange-600' },
                { label: 'Palier 3 — Prix minimum', minKey: 'pricing.tier3Min', ptsKey: 'pricing.tier3Points', color: 'text-yellow-600' },
              ]?.map((tier, i) => {
                const minVal = tier?.minKey?.split('.')?.reduce((o, k) => o?.[k], config);
                const ptsVal = tier?.ptsKey?.split('.')?.reduce((o, k) => o?.[k], config);
                return (
                  <div key={i} className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <NumberField
                      label={tier?.label}
                      hint="Valeur en XOF"
                      value={minVal}
                      onChange={(v) => update(tier?.minKey, v)}
                      min={0}
                      step={1_000_000}
                      suffix="XOF"
                    />
                    <NumberField
                      label="Points attribués"
                      value={ptsVal}
                      onChange={(v) => update(tier?.ptsKey, v)}
                      min={0}
                      max={100}
                      suffix="pts"
                    />
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* Message Length */}
          <SectionCard title="Longueur du Message" icon={Thermometer} iconColor="bg-teal-500">
            <p className="text-xs text-gray-500 mb-4 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              Un message détaillé indique un prospect plus engagé.
            </p>
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <NumberField
                label="Longueur minimale"
                hint="Nombre de caractères requis"
                value={config?.messaging?.minLength}
                onChange={(v) => update('messaging.minLength', v)}
                min={1}
                max={500}
                suffix="chars"
              />
              <NumberField
                label="Points attribués"
                value={config?.messaging?.lengthPoints}
                onChange={(v) => update('messaging.lengthPoints', v)}
                min={0}
                max={100}
                suffix="pts"
              />
            </div>
          </SectionCard>

          {/* Phone Presence */}
          <SectionCard title="Présence du Numéro de Téléphone" icon={Zap} iconColor="bg-green-500">
            <p className="text-xs text-gray-500 mb-4 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              Un prospect qui laisse son numéro est plus facilement joignable et plus engagé.
            </p>
            <div className="max-w-xs p-4 bg-gray-50 rounded-lg border border-gray-200">
              <NumberField
                label="Points si téléphone présent"
                value={config?.phone?.points}
                onChange={(v) => update('phone.points', v)}
                min={0}
                max={100}
                suffix="pts"
              />
            </div>
          </SectionCard>

          {/* GA4 Event Weights */}
          <SectionCard title="Multiplicateurs d'Événements GA4" icon={Sliders} iconColor="bg-blue-500">
            <p className="text-xs text-gray-500 mb-4 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              Points ajoutés au score lorsqu'un événement GA4 spécifique est détecté pour ce lead.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(config?.ga4Weights)?.map(([eventKey, pts]) => (
                <div key={eventKey} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{ga4EventLabels?.[eventKey] || eventKey}</p>
                    <p className="text-xs text-gray-400 font-mono">{eventKey}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={pts}
                      onChange={(e) => update(`ga4Weights.${eventKey}`, Number(e?.target?.value))}
                      className="w-16 border border-gray-300 rounded-md px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    />
                    <span className="text-xs text-gray-500">pts</span>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* Right column: live preview */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden sticky top-4">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 text-sm">Aperçu en Temps Réel</h3>
              <p className="text-xs text-gray-400 mt-0.5">Simulation avec la configuration actuelle</p>
            </div>
            <div className="p-4 space-y-3">
              {previewCases?.map((c, i) => {
                let score = previewScore(c?.price, c?.phone, c?.msgLen, c?.event);
                const { label, cls } = getLabel(score);
                return (
                  <div key={i} className="p-3 rounded-lg border border-gray-100 bg-gray-50">
                    <p className="text-xs text-gray-600 font-medium mb-2 leading-snug">{c?.label}</p>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                      <span className="text-xs font-mono text-gray-500">{score} pts</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Score scale */}
            <div className="px-4 pb-4">
              <p className="text-xs font-medium text-gray-500 mb-2">Échelle de score</p>
              <div className="space-y-1.5">
                {[
                  { label: 'Très Chaud 🔥', min: config?.thresholds?.veryHot, cls: 'bg-red-500' },
                  { label: 'Chaud ⚡', min: config?.thresholds?.hot, cls: 'bg-orange-400' },
                  { label: 'Tiède', min: config?.thresholds?.warm, cls: 'bg-yellow-400' },
                  { label: 'Froid', min: 0, cls: 'bg-blue-400' },
                ]?.map((tier) => (
                  <div key={tier?.label} className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${tier?.cls}`} />
                    <span className="text-xs text-gray-600 flex-1">{tier?.label}</span>
                    <span className="text-xs font-mono text-gray-400">≥ {tier?.min} pts</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Info note */}
            <div className="mx-4 mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-xs text-blue-700 leading-relaxed">
                <strong>Note :</strong> La configuration est sauvegardée localement et appliquée immédiatement aux nouveaux leads sans redémarrage.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadScoringConfigPanel;
